import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

// ── CONFIG ────────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function sbGet(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d : null;
}

// ── HELPERS ───────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("id-ID", {
    day:"numeric", month:"short", year:"numeric",
    hour:"2-digit", minute:"2-digit",
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("id-ID", {
    day:"numeric", month:"long", year:"numeric",
  });
}

// ── CSS ───────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500&family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Josefin+Sans:wght@200;300;400&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html { scroll-behavior:smooth; }
  body { background:#0A0907; color:rgba(240,232,216,.9); font-family:'Josefin Sans',sans-serif; font-weight:300; min-height:100vh; }

  @keyframes ptFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ptSpin   { to{transform:rotate(360deg)} }
  @keyframes ptPulse  { 0%,100%{opacity:.4} 50%{opacity:1} }

  /* ── LAYOUT ── */
  .pt-page  { min-height:100vh; }
  .pt-topbar { height:52px; display:flex; align-items:center; justify-content:space-between; padding:0 28px; border-bottom:1px solid rgba(201,168,76,.07); background:rgba(10,9,7,.9); position:sticky; top:0; z-index:20; backdrop-filter:blur(12px); }
  .pt-topbar-couple { font-family:'Cinzel',serif; font-size:.52rem; letter-spacing:.22em; color:rgba(240,232,216,.75); }
  .pt-topbar-right  { display:flex; align-items:center; gap:12px; }
  .pt-live-dot  { width:7px; height:7px; border-radius:50%; background:#7A9E7E; animation:ptPulse 2s ease infinite; }
  .pt-live-label{ font-family:'Cinzel',serif; font-size:.38rem; letter-spacing:.28em; color:rgba(122,158,126,.7); }
  .pt-refresh-btn { padding:6px 14px; background:transparent; border:1px solid rgba(201,168,76,.15); color:rgba(201,168,76,.55); font-family:'Cinzel',serif; font-size:.4rem; letter-spacing:.18em; cursor:pointer; transition:all .2s; }
  .pt-refresh-btn:hover { background:rgba(201,168,76,.06); }

  .pt-body  { max-width:1020px; margin:0 auto; padding:32px 24px 80px; }

  /* ── HERO STATS ── */
  .pt-hero  { text-align:center; margin-bottom:36px; padding-bottom:32px; border-bottom:1px solid rgba(201,168,76,.07); }
  .pt-hero-tag   { font-family:'Cinzel',serif; font-size:.42rem; letter-spacing:.5em; color:rgba(201,168,76,.44); margin-bottom:8px; }
  .pt-hero-title { font-family:'Cinzel',serif; font-size:clamp(1.4rem,4vw,2.4rem); letter-spacing:.04em; margin-bottom:4px; }
  .pt-hero-bride { color:#C9A84C; font-family:'Cormorant Garamond',serif; font-style:italic; }
  .pt-hero-date  { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:.92rem; color:rgba(240,232,216,.28); margin-bottom:24px; }

  .pt-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
  .pt-stat  { border:1px solid rgba(201,168,76,.1); padding:14px 16px; text-align:center; }
  .pt-stat-val   { font-family:'Cinzel',serif; font-size:clamp(1.4rem,4vw,2.2rem); color:rgba(240,232,216,.9); line-height:1; margin-bottom:5px; }
  .pt-stat-val.green  { color:#7A9E7E; }
  .pt-stat-val.yellow { color:#C9A84C; }
  .pt-stat-val.red    { color:#C97B6F; }
  .pt-stat-label { font-family:'Cinzel',serif; font-size:.36rem; letter-spacing:.32em; color:rgba(201,168,76,.4); }

  /* ── ATTENDANCE BAR ── */
  .pt-attend-bar { height:6px; background:rgba(255,255,255,.06); border-radius:0; margin:20px 0 8px; overflow:hidden; }
  .pt-attend-fill { height:100%; background:linear-gradient(to right,#7A9E7E,#C9A84C); transition:width .8s ease; border-radius:0; }
  .pt-attend-note { display:flex; justify-content:space-between; font-size:.4rem; letter-spacing:.1em; color:rgba(240,232,216,.28); }

  /* ── FILTERS & SEARCH ── */
  .pt-toolbar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:16px; }
  .pt-filter  { padding:6px 14px; background:transparent; border:1px solid rgba(201,168,76,.1); color:rgba(240,232,216,.38); font-family:'Cinzel',serif; font-size:.4rem; letter-spacing:.16em; cursor:pointer; transition:all .2s; }
  .pt-filter:hover  { border-color:rgba(201,168,76,.25); color:rgba(240,232,216,.65); }
  .pt-filter.active { border-color:rgba(201,168,76,.5); color:rgba(201,168,76,.85); background:rgba(201,168,76,.06); }
  .pt-search { flex:1; min-width:180px; max-width:260px; padding:7px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.1); color:rgba(240,232,216,.8); font-family:'Josefin Sans',sans-serif; font-size:.8rem; font-weight:300; outline:none; }
  .pt-search:focus { border-color:rgba(201,168,76,.3); }
  .pt-search::placeholder { color:rgba(240,232,216,.2); }
  .pt-export-btn { padding:7px 16px; background:rgba(201,168,76,.1); border:1px solid rgba(201,168,76,.22); color:rgba(201,168,76,.75); font-family:'Cinzel',serif; font-size:.4rem; letter-spacing:.18em; cursor:pointer; transition:all .2s; margin-left:auto; }
  .pt-export-btn:hover { background:rgba(201,168,76,.18); }

  /* ── GUEST TABLE ── */
  .pt-table-wrap { overflow-x:auto; border:1px solid rgba(201,168,76,.09); animation:ptFadeUp .6s ease both; }
  table { width:100%; border-collapse:collapse; min-width:560px; }
  thead tr { border-bottom:1px solid rgba(201,168,76,.12); background:rgba(201,168,76,.04); }
  th { font-family:'Cinzel',serif; font-size:.36rem; letter-spacing:.28em; color:rgba(201,168,76,.48); padding:11px 14px; text-align:left; white-space:nowrap; }
  tbody tr { border-bottom:1px solid rgba(255,255,255,.04); transition:background .15s; }
  tbody tr:hover { background:rgba(201,168,76,.025); }
  td { padding:11px 14px; font-size:.44rem; letter-spacing:.08em; color:rgba(240,232,216,.62); vertical-align:middle; }
  .td-name    { color:rgba(240,232,216,.88); font-size:.47rem; }
  .td-time    { font-size:.39rem; color:rgba(240,232,216,.28); white-space:nowrap; }
  .td-note    { font-size:.41rem; color:rgba(240,232,216,.35); font-style:italic; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .td-attend  { }

  /* attendance badge */
  .pt-badge { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border:1px solid; border-radius:2px; font-family:'Cinzel',serif; font-size:.35rem; letter-spacing:.14em; white-space:nowrap; }
  .pt-badge.hadir     { color:#7A9E7E; border-color:rgba(122,158,126,.35); background:rgba(122,158,126,.07); }
  .pt-badge.tidak     { color:#C97B6F; border-color:rgba(201,123,111,.35); background:rgba(201,123,111,.07); }
  .pt-badge.mungkin   { color:#C9A84C; border-color:rgba(201,168,76,.35); background:rgba(201,168,76,.07); }
  .pt-badge.pending   { color:rgba(240,232,216,.3); border-color:rgba(255,255,255,.1); background:transparent; }

  /* ── GUESTBOOK TAB ── */
  .pt-gb-list { display:flex; flex-direction:column; gap:8px; animation:ptFadeUp .6s ease both; }
  .pt-gb-card { border:1px solid rgba(201,168,76,.09); padding:16px 18px; }
  .pt-gb-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:8px; }
  .pt-gb-name { font-family:'Cinzel',serif; font-size:.5rem; letter-spacing:.16em; color:rgba(240,232,216,.82); }
  .pt-gb-time { font-size:.38rem; color:rgba(240,232,216,.25); white-space:nowrap; }
  .pt-gb-msg  { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:.88rem; color:rgba(240,232,216,.42); line-height:1.75; }

  /* ── TABS ── */
  .pt-tabs { display:flex; gap:0; border-bottom:1px solid rgba(201,168,76,.1); margin-bottom:22px; }
  .pt-tab  { padding:10px 22px; font-family:'Cinzel',serif; font-size:.44rem; letter-spacing:.2em; color:rgba(240,232,216,.35); cursor:pointer; border-bottom:2px solid transparent; transition:all .2s; background:transparent; border-top:none; border-left:none; border-right:none; }
  .pt-tab:hover  { color:rgba(240,232,216,.65); }
  .pt-tab.active { color:rgba(240,232,216,.88); border-bottom-color:rgba(201,168,76,.6); }
  .pt-tab-badge  { margin-left:7px; font-size:.35rem; background:rgba(201,168,76,.15); color:rgba(201,168,76,.7); padding:1px 6px; border-radius:10px; font-family:'Josefin Sans',sans-serif; letter-spacing:0; }

  /* empty / loading */
  .pt-empty { padding:44px; text-align:center; font-family:'Cinzel',serif; font-size:.48rem; letter-spacing:.26em; color:rgba(240,232,216,.18); }
  .pt-spin  { width:18px; height:18px; border:1px solid rgba(201,168,76,.25); border-top-color:#C9A84C; border-radius:50%; animation:ptSpin 1s linear infinite; display:inline-block; }

  /* not found / no addon */
  .pt-gate { min-height:100vh; display:flex; align-items:center; justify-content:center; flex-direction:column; gap:14px; text-align:center; padding:40px 24px; }
  .pt-gate-icon  { font-size:2.4rem; opacity:.5; }
  .pt-gate-title { font-family:'Cinzel',serif; font-size:1.3rem; letter-spacing:.06em; color:rgba(240,232,216,.6); }
  .pt-gate-sub   { font-size:.44rem; letter-spacing:.1em; color:rgba(240,232,216,.25); line-height:1.85; }

  /* auto-refresh indicator */
  .pt-auto-label { font-size:.38rem; letter-spacing:.1em; color:rgba(240,232,216,.22); }

  @media(max-width:640px) {
    .pt-stats { grid-template-columns:1fr 1fr; }
    .pt-topbar-couple { font-size:.44rem; }
    .pt-body { padding:24px 16px 60px; }
  }
  @media(max-width:400px) {
    .pt-stats { grid-template-columns:1fr; }
  }
`;

// ── EXPORT CSV ────────────────────────────────────────────────
function exportCsv(rsvps, guestbook, couple) {
  const rows = [
    ["Nama","Kehadiran","Jumlah Tamu","Pesan","Waktu RSVP"],
    ...rsvps.map(r => [
      r.name,
      r.attendance,
      r.guest_count || 1,
      (r.note || "").replace(/,/g," "),
      fmt(r.created_at),
    ]),
  ];
  const csv = rows.map(r => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `rsvp-${couple}-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── ATTENDANCE BADGE ──────────────────────────────────────────
function AttendBadge({ val }) {
  const map = {
    hadir:        { label:"Hadir",      cls:"hadir",   icon:"✓" },
    tidak_hadir:  { label:"Tidak Hadir",cls:"tidak",   icon:"✗" },
    mungkin:      { label:"Mungkin",    cls:"mungkin", icon:"?" },
  };
  const m = map[val] || { label:"—", cls:"pending", icon:"·" };
  return <span className={`pt-badge ${m.cls}`}>{m.icon} {m.label}</span>;
}

// ── MAIN ─────────────────────────────────────────────────────
export default function Pantau() {
  const { slug } = useParams();

  const [wedding,   setWedding]   = useState(null);
  const [rsvps,     setRsvps]     = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [noAddon,   setNoAddon]   = useState(false);
  const [tab,       setTab]       = useState("rsvp");
  const [filter,    setFilter]    = useState("semua");
  const [search,    setSearch]    = useState("");
  const intervalRef = useRef(null);

  // ── Fetch wedding ──
  const fetchWedding = useCallback(async () => {
    const data = await sbGet(`weddings?slug=eq.${slug}&limit=1`);
    if (!data || data.length === 0) { setNotFound(true); setLoading(false); return; }
    const w = data[0];
    if (!w.addons?.includes("pantau_rsvp")) { setNoAddon(true); setLoading(false); return; }
    setWedding(w);
    return w;
  }, [slug]);

  // ── Fetch rsvps & guestbook ──
  const fetchData = useCallback(async (w) => {
    const target = w || wedding;
    if (!target) return;
    const [rsvpData, gbData] = await Promise.all([
      sbGet(`rsvps?wedding_id=eq.${target.id}&order=created_at.desc`),
      sbGet(`guestbook?wedding_id=eq.${target.id}&order=created_at.desc`),
    ]);
    setRsvps(rsvpData || []);
    setGuestbook(gbData || []);
    setLoading(false);
  }, [wedding]);

  useEffect(() => {
    fetchWedding().then(w => { if (w) fetchData(w); });
  }, [fetchWedding, fetchData]);

  // Auto-refresh setiap 30 detik
  useEffect(() => {
    if (!wedding) return;
    intervalRef.current = setInterval(() => fetchData(), 30000);
    return () => clearInterval(intervalRef.current);
  }, [wedding, fetchData]);

  // ── Stats ──
  const hadir   = rsvps.filter(r => r.attendance === "hadir");
  const tidak   = rsvps.filter(r => r.attendance === "tidak_hadir");
  const mungkin = rsvps.filter(r => r.attendance === "mungkin");
  const totalGuests = hadir.reduce((s,r) => s + (r.guest_count || 1), 0);
  const attendRate  = rsvps.length > 0 ? Math.round(hadir.length / rsvps.length * 100) : 0;

  // ── Filtered rsvps ──
  const filteredRsvps = rsvps.filter(r => {
    const matchFilter = filter === "semua" || r.attendance === filter;
    const matchSearch = !search || r.name?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  // ── GUARD: loading ──
  if (loading) return (
    <div style={{background:"#0A0907",minHeight:"100vh"}}>
      <style>{CSS}</style>
      <div className="pt-gate">
        <div className="pt-gate-icon"><span className="pt-spin"/></div>
      </div>
    </div>
  );

  // ── GUARD: not found ──
  if (notFound) return (
    <div style={{background:"#0A0907",minHeight:"100vh"}}>
      <style>{CSS}</style>
      <div className="pt-gate">
        <div className="pt-gate-icon">✦</div>
        <div className="pt-gate-title">Halaman Tidak Ditemukan</div>
        <p className="pt-gate-sub">Periksa kembali link pantau RSVP kamu.</p>
      </div>
    </div>
  );

  // ── GUARD: no addon ──
  if (noAddon) return (
    <div style={{background:"#0A0907",minHeight:"100vh"}}>
      <style>{CSS}</style>
      <div className="pt-gate">
        <div className="pt-gate-icon">🔒</div>
        <div className="pt-gate-title">Fitur Belum Aktif</div>
        <p className="pt-gate-sub">
          Pantau RSVP adalah add-on berbayar (Rp 15.000).<br/>
          Tambahkan saat checkout atau hubungi admin untuk upgrade.
        </p>
      </div>
    </div>
  );

  const couple = `${wedding.groom_name}-${wedding.bride_name}`;

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════
  return (
    <div className="pt-page" style={{background:"#0A0907"}}>
      <style>{CSS}</style>

      {/* Topbar */}
      <div className="pt-topbar">
        <div className="pt-topbar-couple">
          {wedding.groom_name} &amp; {wedding.bride_name}
        </div>
        <div className="pt-topbar-right">
          <div className="pt-live-dot"/>
          <span className="pt-live-label">LIVE</span>
          <span className="pt-auto-label">· Auto-refresh 30s</span>
          <button className="pt-refresh-btn" onClick={() => fetchData()}>↻</button>
        </div>
      </div>

      <div className="pt-body">

        {/* Hero stats */}
        <div className="pt-hero">
          <div className="pt-hero-tag">✦ PANTAU RSVP ✦</div>
          <h1 className="pt-hero-title">
            {wedding.groom_name}
            <span style={{opacity:.2, margin:"0 12px", fontFamily:"serif"}}>&</span>
            <span className="pt-hero-bride">{wedding.bride_name}</span>
          </h1>
          <div className="pt-hero-date">{fmtDate(wedding.wedding_date)}</div>

          <div className="pt-stats">
            {[
              [rsvps.length,   "",       "Total RSVP"],
              [hadir.length,   "green",  "Hadir"],
              [tidak.length,   "red",    "Tidak Hadir"],
              [mungkin.length, "yellow", "Mungkin"],
            ].map(([v, cls, label]) => (
              <div key={label} className="pt-stat">
                <div className={`pt-stat-val${cls?" "+cls:""}`}>{v}</div>
                <div className="pt-stat-label">{label}</div>
              </div>
            ))}
          </div>

          {rsvps.length > 0 && (
            <>
              <div className="pt-attend-bar">
                <div className="pt-attend-fill" style={{width:`${attendRate}%`}}/>
              </div>
              <div className="pt-attend-note">
                <span>{attendRate}% konfirmasi hadir</span>
                <span>{totalGuests} total tamu hadir</span>
              </div>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="pt-tabs">
          <button className={`pt-tab${tab==="rsvp"?" active":""}`}
            onClick={() => setTab("rsvp")}>
            RSVP
            <span className="pt-tab-badge">{rsvps.length}</span>
          </button>
          <button className={`pt-tab${tab==="gb"?" active":""}`}
            onClick={() => setTab("gb")}>
            Buku Tamu
            <span className="pt-tab-badge">{guestbook.length}</span>
          </button>
        </div>

        {/* ── TAB: RSVP ── */}
        {tab === "rsvp" && (
          <>
            <div className="pt-toolbar">
              {["semua","hadir","tidak_hadir","mungkin"].map(f => (
                <button key={f}
                  className={`pt-filter${filter===f?" active":""}`}
                  onClick={() => setFilter(f)}>
                  {f.charAt(0).toUpperCase()+f.slice(1)}
                  {f !== "semua" && (
                    <span style={{marginLeft:5,opacity:.6}}>
                      ({rsvps.filter(r=>r.attendance===f).length})
                    </span>
                  )}
                </button>
              ))}
              <input className="pt-search"
                placeholder="Cari nama tamu..."
                value={search} onChange={e => setSearch(e.target.value)}/>
              <button className="pt-export-btn"
                onClick={() => exportCsv(rsvps, guestbook, couple)}
                title="Export ke CSV">
                ↓ Export CSV
              </button>
            </div>

            <div className="pt-table-wrap">
              {filteredRsvps.length === 0 ? (
                <div className="pt-empty">
                  {rsvps.length === 0 ? "Belum ada RSVP masuk" : "Tidak ada hasil"}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>NAMA TAMU</th>
                      <th>KEHADIRAN</th>
                      <th>JML TAMU</th>
                      <th>PESAN</th>
                      <th>WAKTU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRsvps.map((r, i) => (
                      <tr key={r.id}>
                        <td style={{color:"rgba(240,232,216,.25)",fontSize:".4rem"}}>{i+1}</td>
                        <td className="td-name">{r.name}</td>
                        <td className="td-attend"><AttendBadge val={r.attendance}/></td>
                        <td style={{textAlign:"center",fontFamily:"'Cinzel',serif",color:"rgba(240,232,216,.65)"}}>
                          {r.guest_count || 1}
                        </td>
                        <td className="td-note">{r.note || <span style={{opacity:.25}}>—</span>}</td>
                        <td className="td-time">{fmt(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Summary bawah */}
            {hadir.length > 0 && (
              <div style={{marginTop:16,padding:"12px 16px",border:"1px solid rgba(201,168,76,.08)",
                           display:"flex",gap:28,flexWrap:"wrap"}}>
                <div style={{fontSize:".42rem",letterSpacing:".1em",color:"rgba(240,232,216,.35)"}}>
                  Estimasi kursi:{" "}
                  <span style={{fontFamily:"'Cinzel',serif",color:"#C9A84C"}}>
                    {totalGuests} tamu
                  </span>
                </div>
                <div style={{fontSize:".42rem",letterSpacing:".1em",color:"rgba(240,232,216,.35)"}}>
                  Belum konfirmasi:{" "}
                  <span style={{fontFamily:"'Cinzel',serif",color:"rgba(240,232,216,.6)"}}>
                    {mungkin.length} tamu
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TAB: BUKU TAMU ── */}
        {tab === "gb" && (
          <div className="pt-gb-list">
            {guestbook.length === 0 ? (
              <div className="pt-empty">Belum ada pesan di buku tamu</div>
            ) : (
              guestbook.map(g => (
                <div key={g.id} className="pt-gb-card">
                  <div className="pt-gb-head">
                    <div className="pt-gb-name">{g.name}</div>
                    <div className="pt-gb-time">{fmt(g.created_at)}</div>
                  </div>
                  <div className="pt-gb-msg">"{g.message}"</div>
                </div>
              ))
            )}
          </div>
        )}

      </div>
    </div>
  );
}
